<template>
  <q-select
    v-model="selectedOptions"
    class="multiselect-standard"
    :class="classes"
    :style="styles"
    multiple
    use-input
    borderless
    no-error-icon
    hide-bottom-space
    dropdown-icon="mono-chevron-down"
    behavior="menu"
    :input-debounce="inputDebounce"

    :loading="loading"
    :disable="disabled"
    :placeholder="placeholder"
    :name="name"
    :tabindex="tabindex"
    :popup-content-class="popupClasses"
    :popup-no-route-dismiss="popupNoRouteDismiss"
    :max-values="maxValues"

    :options="filteredOptions"
    :option-label="optionLabel"
    :option-value="optionValue"
    :option-disable="optionDisable"
    options-selected-class="is-selected"

    @filter="onFilter"
    @add="onAdd"
    @remove="onRemove"
  >
    <template #selected-item="{ index, removeAtIndex }">
      <Chip
        class="multiselect-chip"
        :size="size"
        :shape="shape"
        :label="getOptionLabel(index)"
        removable
        @remove="removeAtIndex(index)"
      />
    </template>

    <template #append>
      <Button
        v-if="clearable"
        class="multiselect-clear"
        :size="size"
        variant="text"
        start-icon="mono-cross"
        is-icon
        @click.stop="onClear"
      />
      <slot name="append" />
    </template>
  </q-select>
</template>

<script setup>
import { computed, ref } from 'vue'
import { cx } from '@/helpers'
import Chip from '@/components/ui/Chip/Chip.vue'
import Button from '@/components/ui/Button/Button.vue'

defineOptions({ name: 'Multiselect' })

const selectedOptions = defineModel({ type: Array, default: () => [] })
const emit = defineEmits(['update:options', 'add', 'remove'])
const props = defineProps({
  options: { type: Array, required: true },
  optionLabel: { type: String, default: 'label' },
  optionValue: { type: String, default: 'value' },
  optionDisable: { type: String, default: 'disable' },

  size: {
    type: String,
    default: 'md',
    validator: v => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v)
  },
  shape: {
    type: String,
    default: 'rounded',
    validator: v => ['square', 'rounded'].includes(v)
  },
  fullwidth: { type: Boolean, default: false },
  inputDebounce: { type: [String, Number], default: 100 },
  clearable: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  placeholder: { type: String, default: null },
  name: { type: String, default: null },
  tabindex: { type: [String, Number], default: null },
  popupContentClass: { type: [String, Array, Object], default: null },
  popupNoRouteDismiss: { type: Boolean, default: false },
  maxValues: { type: Number, default: undefined }
})

if (!props.options || !Array.isArray(props.options)) {
  console.error('Wrong or missing \'options\' property')
}

const minSizes = {
  xs: 24,
  sm: 30,
  md: 36,
  lg: 42,
  xl: 48
}

const filterQuery = ref('')
const filteredOptions = computed(() => {
  if (!filterQuery.value) return props.options
  const needle = filterQuery.value.toLowerCase()
  return props.options.filter(opt => {
    const val = typeof opt === 'object' ? opt[props.optionValue] : opt
    return String(val).toLowerCase().includes(needle)
  })
})

const classes = computed(() => cx(
  `size-${props.size}`,
  `shape-${props.shape}`,
  props.fullwidth && 'is-full-width',
  selectedOptions.value.length && 'has-selected'
))
const styles = computed(() => ({ '--select-min-height': `${minSizes[props.size]}px` }))
const popupClasses = computed(() => cx(
  'multiselect--popup',
  selectedOptions.value.length && 'has-selected',
  props.popupContentClass
))

const getOptionLabel = i => {
  const opt = props.options[i]
  const label = typeof opt === 'object' ? opt[props.optionLabel] : opt
  return label
}

const onFilter = (val, update) => {
  filterQuery.value = val
  update()
}

const onAdd = ({ index, value }) => {
  emit('add', { index, option: value })
}
const onRemove = ({ index, value }) => {
  emit('remove', { index, option: value })
}
const onClear = () => {

}
</script>

<style>
@reference "#app-styles.css";

.multiselect--popup {
  @apply surface-float shadow-lg rounded-lg overflow-hidden;
}
</style>

<style scoped>
@reference "#app-styles.css";

.multiselect-standard {
  @apply inline-flex items-stretch w-auto h-auto min-h-0 font-medium;
  @apply border edge-base cursor-pointer select-none transition-colors;

  &:not(.is-disabled):hover { @apply surface-surf; }
  &.q-field--focused { @apply edge-primary-faint; }

  &.shape-square  { @apply rounded-md; }
  &.shape-rounded { @apply rounded-[calc(var(--select-min-height)/2)]; }

  &.is-full-width { @apply w-full; }

  &:deep(.q-field__inner)  { @apply flex-1 p-0 min-h-0; }
  &:deep(.q-field__bottom) { @apply hidden; }
  &:deep(.q-field__control) { @apply flex-wrap border-none! min-h-(--select-min-height) bg-transparent cursor-pointer; }
  &:deep(.q-field__native) { @apply leading-[inherit] flex-wrap min-w-0 min-h-0; }
  &:deep(input.q-field__input) { @apply leading-[inherit] ink-soft min-w-auto min-h-max; }
  &:deep(input::placeholder) { @apply leading-[inherit] ink-disabled; }
  &:deep(.q-field__marginal) { @apply h-(--select-min-height) min-h-0; }
  &:deep(.q-select__dropdown-icon) { @apply ink-subtle transition-transform duration-200 self-start; }
  &.q-field--focused:deep(.q-select__dropdown-icon) { @apply rotate-180; }

  &.size-xs {
    @apply text-[10px]! leading-3;
    &:deep(.q-field__control) { @apply px-1; }
    &:not(.has-selected) :deep(.q-field__control) { @apply pl-2; }
    &:deep(.q-field__native) { @apply gap-0.5 py-1; }
    &:deep(.q-field__input) { @apply min-h-4; }
    &:deep(.q-field__marginal) { @apply py-1; }
    &:deep(.q-select__dropdown-icon) { @apply text-[12px] leading-3 h-4 w-4; }
  }
  &.size-sm {
    @apply text-[12px]! leading-3.5;
    &:deep(.q-field__control) { @apply px-1.25; }
    &:not(.has-selected) :deep(.q-field__control) { @apply pl-2.5; }
    &:deep(.q-field__native) { @apply gap-1 py-1.25; }
    &:deep(.q-field__input) { @apply min-h-5; }
    &:deep(.q-field__marginal) { @apply py-1.25; }
    &:deep(.q-select__dropdown-icon) { @apply text-[14px] leading-3.5 h-5 w-5; }
  }
  &.size-md {
    @apply text-[14px]! leading-4;
    &:deep(.q-field__control) { @apply px-1.5; }
    &:not(.has-selected) :deep(.q-field__control) { @apply pl-3; }
    &:deep(.q-field__native) { @apply gap-1.25 py-1.5; }
    &:deep(.q-field__input) { @apply min-h-6; }
    &:deep(.q-field__marginal) { @apply py-1.5; }
    &:deep(.q-select__dropdown-icon) { @apply text-[16px] leading-4 h-6 w-6; }
  }
  &.size-lg {
    @apply text-[16px] leading-4.5;
    &:deep(.q-field__control) { @apply px-1.75; }
    &:not(.has-selected) :deep(.q-field__control) { @apply pl-3.5; }
    &:deep(.q-field__native) { @apply gap-1.5 py-1.75; }
    &:deep(.q-field__input) { @apply min-h-7; }
    &:deep(.q-field__marginal) { @apply py-1.75; }
    &:deep(.q-select__dropdown-icon) { @apply text-[18px] leading-4.5 h-7 w-7; }
  }
  &.size-xl {
    @apply text-[18px] leading-4;
    &:deep(.q-field__control) { @apply px-2; }
    &:not(.has-selected) :deep(.q-field__control) { @apply pl-4; }
    &:deep(.q-field__native) { @apply gap-1.75 py-2; }
    &:deep(.q-field__input) { @apply min-h-8; }
    &:deep(.q-field__marginal) { @apply py-2; }
    &:deep(.q-select__dropdown-icon) { @apply text-[20px] leading-5 h-8 w-8; }
  }
}
</style>
